/**
 * User Snapshot Service
 * 
 * High-level service for managing user snapshots throughout the application.
 * Provides easy-to-use functions for creating, retrieving, and sharing snapshots.
 */

import { v4 as uuidv4 } from 'uuid';
import { 
  saveUserSnapshot, 
  getUserSnapshot, 
  updateUserSnapshot, 
  markSnapshotAsUsed,
  createTimestamp,
  timestampToDate,
  UserSnapshot,
  CreateSnapshotPayload
} from './api';
import { useApp } from '@/context/AppContext';
import { getBundleInfo } from '@/hooks/useBundleSelection';

// Bundle info interface (moved from firebase.ts)
export interface BundleInfo {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  isShopifyBundle: boolean;
  components: Array<{
    name: string;
    type: string;
  }>;
  bundleMapping?: {
    shopifyHandle: string;
    variants: {
      S: string;
      M: string;
      L: string;
    };
  };
}

// Client-side snapshot creation interface
export interface CreateSnapshotParams {
  stage: 'productos' | 'plan-seleccion' | 'carrito';
  sessionId?: string;
  expirationHours?: number;
  bundleInfo?: BundleInfo;
}

// Client-side snapshot data interface  
export interface SnapshotData {
  id: string;
  url: string;
  userData: UserSnapshot['userData'];
  metadata: UserSnapshot['metadata'];
  createdAt: Date;
  expiresAt: Date;
  status: UserSnapshot['status'];
}

/**
 * Create a new user snapshot from current app state
 */
export const createUserSnapshot = async (
  appState: ReturnType<typeof useApp>['state'],
  params: CreateSnapshotParams
): Promise<SnapshotData> => {
  const sessionId = params.sessionId || uuidv4();
  const expirationHours = params.expirationHours || 24;
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + expirationHours);

  // Extract user data from app state
  const { eleccion } = appState;
  
  // Create snapshot data for API
  const snapshotData: CreateSnapshotPayload = {
    sessionId,
    userData: {
      maskSizing: eleccion.maskSizing,
      equipoSeleccionado: eleccion.equipoSeleccionado,
      mascarillaSeleccionada: eleccion.mascarillaSeleccionada,
      bunoIncluido: eleccion.bunoIncluido,
      bundleInfo: params.bundleInfo
    },
    metadata: {
      userAgent: navigator.userAgent,
      ipAddress: undefined, // Will be populated server-side if needed
      stage: params.stage,
      completedSteps: eleccion.completedSteps,
      breathingSelfReport: eleccion.breathingSelfReport,
      noseScore: eleccion.noseScore,
      maskRecommendation: eleccion.maskRecommendation
    },
    expiresAt: createTimestamp(expiresAt),
    status: 'active'
  };

  try {
    // Save via API
    const snapshotId = await saveUserSnapshot(snapshotData);
    
    // Create URL
    const url = `${window.location.origin}/snapshot/${snapshotId}`;
    
    console.log(`📸 User snapshot created: ${snapshotId}`);
    console.log(`🔗 Snapshot URL: ${url}`);
    
    return {
      id: snapshotId,
      url,
      userData: snapshotData.userData,
      metadata: snapshotData.metadata,
      createdAt: new Date(),
      expiresAt,
      status: 'active'
    };
    
  } catch (error) {
    console.error('Failed to create user snapshot:', error);
    throw new Error('Failed to create user snapshot. Please try again.');
  }
};

/**
 * Retrieve a user snapshot by ID
 */
export const retrieveUserSnapshot = async (snapshotId: string): Promise<SnapshotData | null> => {
  try {
    const snapshot = await getUserSnapshot(snapshotId);
    
    if (!snapshot) {
      return null;
    }
    
    return {
      id: snapshot.id,
      url: `${window.location.origin}/snapshot/${snapshot.id}`,
      userData: snapshot.userData,
      metadata: snapshot.metadata,
      createdAt: timestampToDate(snapshot.timestamp),
      expiresAt: timestampToDate(snapshot.expiresAt),
      status: snapshot.status
    };
    
  } catch (error) {
    console.error('Failed to retrieve user snapshot:', error);
    throw new Error('Failed to retrieve user snapshot.');
  }
};

/**
 * Update snapshot when user progresses to next stage
 */
export const updateSnapshotStage = async (
  snapshotId: string,
  stage: 'productos' | 'plan-seleccion' | 'carrito',
  appState: ReturnType<typeof useApp>['state'],
  bundleInfo?: BundleInfo
): Promise<void> => {
  try {
    const { eleccion } = appState;
    
    const updates: Partial<UserSnapshot> = {
      userData: {
        maskSizing: eleccion.maskSizing,
        equipoSeleccionado: eleccion.equipoSeleccionado,
        mascarillaSeleccionada: eleccion.mascarillaSeleccionada,
        bunoIncluido: eleccion.bunoIncluido,
        bundleInfo
      },
      metadata: {
        userAgent: navigator.userAgent,
        stage,
        completedSteps: eleccion.completedSteps,
        breathingSelfReport: eleccion.breathingSelfReport,
        noseScore: eleccion.noseScore,
        maskRecommendation: eleccion.maskRecommendation
      }
    };
    
    await updateUserSnapshot(snapshotId, updates);
    console.log(`📝 Snapshot ${snapshotId} updated to stage: ${stage}`);
    
  } catch (error) {
    console.error('Failed to update snapshot stage:', error);
    throw new Error('Failed to update snapshot.');
  }
};

/**
 * Share snapshot URL (copy to clipboard)
 */
export const shareSnapshot = async (snapshotData: SnapshotData): Promise<void> => {
  try {
    await navigator.clipboard.writeText(snapshotData.url);
    console.log(`📋 Snapshot URL copied to clipboard: ${snapshotData.url}`);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // Fallback - create a temporary text area
    const textArea = document.createElement('textarea');
    textArea.value = snapshotData.url;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      console.log(`📋 Snapshot URL copied to clipboard (fallback): ${snapshotData.url}`);
    } catch (fallbackError) {
      console.error('Fallback clipboard copy failed:', fallbackError);
      throw new Error('Unable to copy URL to clipboard');
    }
    
    document.body.removeChild(textArea);
  }
};

/**
 * Generate shareable message for WhatsApp, email, etc.
 */
export const generateShareMessage = (snapshotData: SnapshotData): string => {
  const { userData } = snapshotData;
  const equipo = userData.equipoSeleccionado?.modelo || 'CPAP';
  const mascarilla = userData.mascarillaSeleccionada?.modelo || 'Mascarilla';
  
  const message = `¡Mira mi selección de equipos CPAP! 
  
🔹 Equipo: ${equipo}
🔹 Mascarilla: ${mascarilla}
🔹 Programa buno+: ${userData.bunoIncluido ? 'Incluido' : 'No incluido'}

Ver selección completa: ${snapshotData.url}

#CPAP #SaludRespiratoria #buno`;

  return message;
};

/**
 * Mark snapshot as used (when user completes purchase)
 */
export const markSnapshotUsed = async (snapshotId: string): Promise<void> => {
  try {
    await markSnapshotAsUsed(snapshotId);
    console.log(`✅ Snapshot ${snapshotId} marked as used`);
  } catch (error) {
    console.error('Failed to mark snapshot as used:', error);
    // Don't throw error as this is not critical for user experience
  }
};

/**
 * Restore app state from snapshot
 */
export const restoreFromSnapshot = (
  snapshotData: SnapshotData,
  updateEleccionProgress: ReturnType<typeof useApp>['updateEleccionProgress'],
  updateDispositivo: ReturnType<typeof useApp>['updateDispositivo']
): void => {
  try {
    const { userData, metadata } = snapshotData;
    
    // Restore election progress
    updateEleccionProgress({
      maskSizing: userData.maskSizing,
      equipoSeleccionado: userData.equipoSeleccionado,
      mascarillaSeleccionada: userData.mascarillaSeleccionada,
      bunoIncluido: userData.bunoIncluido,
      completedSteps: metadata.completedSteps,
      breathingSelfReport: metadata.breathingSelfReport || '',
      noseScore: metadata.noseScore || null,
      maskRecommendation: metadata.maskRecommendation || '',
      currentStep: metadata.stage
    });
    
    // Restore device selection
    if (userData.equipoSeleccionado) {
      updateDispositivo(userData.equipoSeleccionado.marca);
    }
    
    console.log(`🔄 App state restored from snapshot: ${snapshotData.id}`);
    
  } catch (error) {
    console.error('Failed to restore from snapshot:', error);
    throw new Error('Failed to restore user data from snapshot.');
  }
};

/**
 * Validate snapshot URL format
 */
export const validateSnapshotUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathMatch = urlObj.pathname.match(/^\/snapshot\/([a-f0-9-]+)$/);
    
    if (pathMatch) {
      return pathMatch[1]; // Return the snapshot ID
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * Check if user is in snapshot recovery mode
 */
export const isSnapshotMode = (): string | null => {
  const pathMatch = window.location.pathname.match(/^\/snapshot\/([a-f0-9-]+)$/);
  return pathMatch ? pathMatch[1] : null;
};